import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAudioPreviewReturn {
  isPlaying: boolean
  playbackProgress: number
  play: () => void
  pause: () => void
  toggle: () => void
}

// Drives playback of a recorded blob URL with an HTMLAudioElement.
// playbackProgress is 0..1, used by the waveform to color the played slice.
export const useAudioPreview = (audioUrl: string | null): UseAudioPreviewReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackProgress, setPlaybackProgress] = useState(0)

  useEffect(() => {
    if (!audioUrl) {
      audioRef.current = null
      setIsPlaying(false)
      setPlaybackProgress(0)
      return
    }

    const audio = new Audio(audioUrl)
    audioRef.current = audio

    const onTimeUpdate = () => {
      if (!audio.duration || !Number.isFinite(audio.duration)) return
      setPlaybackProgress(audio.currentTime / audio.duration)
    }
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const onEnded = () => {
      setIsPlaying(false)
      setPlaybackProgress(0)
      audio.currentTime = 0
    }

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)

    return () => {
      audio.pause()
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audioRef.current = null
      setIsPlaying(false)
      setPlaybackProgress(0)
    }
  }, [audioUrl])

  const play = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    void audio.play().catch((err) => {
      console.error('Audio preview play failed:', err)
    })
  }, [])

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const toggle = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      void audio.play().catch((err) => {
        console.error('Audio preview play failed:', err)
      })
    } else {
      audio.pause()
    }
  }, [])

  return { isPlaying, playbackProgress, play, pause, toggle }
}
